import React, { useState, useEffect, useCallback } from 'react';
import { Modal, message } from 'antd';
import { FC116 } from '../../../services/scApi';
import { scApi } from '../../../services/scApi';

interface InspectionSpecModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (inspections: Partial<FC116>[]) => void;
  inspections: Partial<FC116>[];
  scNo: string;
  itemSeq: number;
}

const InspectionSpecModal: React.FC<InspectionSpecModalProps> = ({
  open, onClose, onSave, inspections, scNo, itemSeq,
}) => {
  const [rows, setRows] = useState<Partial<FC116>[]>([]);
  const [testNames, setTestNames] = useState<Record<string, string>>({});

  // Reset rows when modal opens
  useEffect(() => {
    if (open) {
      const init = inspections.length > 0
        ? inspections.map(c => ({ ...c }))
        : [];
      setRows(init);
      // Resolve test names for existing rows
      const namesMap: Record<string, string> = {};
      const lookups = init
        .filter(r => r.fc116_11)
        .map(r =>
          scApi.lookupName('testCode', r.fc116_11!).then(name => {
            namesMap[r.fc116_11!] = name;
          })
        );
      Promise.all(lookups).then(() => setTestNames(namesMap));
    }
  }, [open, inspections]);

  const addRow = useCallback(() => {
    const maxSeq = rows.reduce((max, r) => Math.max(max, r.fc116_03 || 0), 0);
    setRows(prev => [
      ...prev,
      {
        fc116_01: scNo,
        fc116_02: itemSeq,
        fc116_03: maxSeq + 1,
        fc116_11: '',
        fc116_13: '',
      },
    ]);
  }, [rows, scNo, itemSeq]);

  const deleteRow = useCallback((idx: number) => {
    setRows(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((r, i) => ({ ...r, fc116_03: i + 1 }));
    });
  }, []);

  const updateField = useCallback((idx: number, key: keyof FC116, value: FC116[keyof FC116]) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  }, []);

  const handleTestCodeBlur = useCallback((idx: number, code: string) => {
    if (!code.trim()) return;
    scApi.lookupName('testCode', code.trim()).then(name => {
      setTestNames(prev => ({ ...prev, [code.trim()]: name }));
    });
  }, []);

  const handleOk = () => {
    // BR-V40: FC116_11 if non-empty must exist in lookup (valid name)
    for (let i = 0; i < rows.length; i++) {
      const code = (rows[i].fc116_11 || '').trim();
      if (code && !testNames[code]) {
        message.error(`第 ${i + 1} 行：查無此測試代號!`);
        return;
      }
    }

    onSave(rows);
  };

  return (
    <Modal
      title={`檢驗規範 - 項次 ${itemSeq}`}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      width={700}
      okText="確認"
      cancelText="取消"
      destroyOnClose
    >
      <div style={{ marginBottom: 8 }}>
        <button
          className="btn btn-success btn-sm"
          onClick={addRow}
          style={{ marginRight: 8 }}
        >
          + 新增檢驗
        </button>
      </div>

      <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ width: 40, padding: '6px 8px', textAlign: 'center', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>序</th>
            <th style={{ width: 100, padding: '6px 8px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>測試代號</th>
            <th style={{ width: 120, padding: '6px 8px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>測試名稱</th>
            <th style={{ padding: '6px 8px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>測試說明</th>
            <th style={{ width: 50, padding: '6px 8px', textAlign: 'center', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: 'center', padding: '16px 8px', color: '#999' }}>
                尚無檢驗規範，請點擊「+ 新增檢驗」
              </td>
            </tr>
          )}
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                {r.fc116_03 || i + 1}
              </td>
              <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>
                <input
                  type="text"
                  style={{ width: '100%', height: 28, fontSize: 13, border: '1px solid #d9d9d9', borderRadius: 4, padding: '0 8px' }}
                  value={r.fc116_11 || ''}
                  onChange={e => updateField(i, 'fc116_11', e.target.value)}
                  onBlur={e => handleTestCodeBlur(i, e.target.value)}
                />
              </td>
              <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
                {(() => {
                  const code = (r.fc116_11 || '').trim();
                  const name = testNames[code];
                  if (!code) return <span style={{ color: '#666' }}>--</span>;
                  if (name) return <span style={{ color: '#666' }}>{name}</span>;
                  if (code in testNames) return <span style={{ color: '#ff4d4f' }}>*查無此代號*</span>;
                  return <span style={{ color: '#666' }}>--</span>;
                })()}
              </td>
              <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>
                <input
                  type="text"
                  style={{ width: '100%', height: 28, fontSize: 13, border: '1px solid #d9d9d9', borderRadius: 4, padding: '0 8px' }}
                  value={r.fc116_13 || ''}
                  onChange={e => updateField(i, 'fc116_13', e.target.value)}
                />
              </td>
              <td style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                <button
                  className="btn btn-danger btn-sm"
                  style={{ height: 24, padding: '0 8px', fontSize: 12 }}
                  onClick={() => deleteRow(i)}
                >
                  刪除
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
};

export default React.memo(InspectionSpecModal);
